import styled from 'styled-components';
import { Collapse, CollapseProps } from 'antd';
import { MainHead } from '../../../components/common/head/MainHead';
import { managedFarmPageInfo } from '../../../constants/pageInfo/pageInfo.constant';
import { useLayout } from '../../../hooks/screen/useLayout';
import HERO_IMAGE_NO_COMMISSION_FEE from '../../../assets/images/managedFarm/no_commission_fee.png';
import { Chip } from '../../../components/common/chip/Chip';
import { Button } from '../../../components/common/buttons/Button';
import { Icon } from '../../../components/common/icon/Icon';
import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ampli } from '../../../ampli';

export const ManagedFarmEntryPage = () => {
  const navigate = useNavigate();
  const panelStyle: React.CSSProperties = {
    border: 'none',
    paddingTop: 24,
  };

  const items: CollapseProps['items'] = [
    {
      key: 'what-is-managed-farm',
      label: <Question>{'플루바 마켓 ‘대행반장’이 무엇인가요?'}</Question>,
      children: (
        <Answer>
          {
            '대행반장은 필지 기반으로 농작업을 보다 편리하게 관리할 수 있는 기능입니다. 필지 면적 확인부터 작업 등록, 필지 소유자와의 간편한 연락까지 한 번에 처리할 수 있습니다.'
          }
        </Answer>
      ),
      style: panelStyle,
    },
    {
      key: 'check-land-area-for-free',
      label: <Question>{'필지 평수 확인은 무료인가요?'}</Question>,
      children: <Answer>{'네, 대행반장에서는 필지 면적(㎡, 평수) 확인 기능을 무료로 제공하고 있습니다.'}</Answer>,
      style: panelStyle,
    },
  ];

  useLayout({
    fixedHeader: (
      <MainHead
        backButton={{ isShow: true }}
        textLink={{
          text: '간편 평수 확인하기',
          path: managedFarmPageInfo.searchFieldPage.path,
          onClick: () => {
            ampli.pvmClickGoToSearchFieldButton();
          },
        }}
      />
    ),
    fixedFooter: <></>,
    fixedHeaderHeight: 48,
    fixedFooterHeight: 0,
    shouldShowFooter: false,
  });

  return (
    <Container>
      <MainSection>
        <HeroSection>
          <HeroTitleGroup>
            <HeroTitle>{'지도로 한눈에 보는\n대행 농작업 관리'}</HeroTitle>
            <HeroTitleImage imageUrl={HERO_IMAGE_NO_COMMISSION_FEE} />
          </HeroTitleGroup>
          <HeroBenefitGroup>
            <ChipContainer>
              <Chip
                type={'round'}
                theme={'light'}
                color={'grey'}
                size={'lg'}
                icon={{ name: 'Check', position: 'left', size: 'Large', fill: '#34C77E' }}
              >
                {'논 평수 측정'}
              </Chip>
              <Chip
                type={'round'}
                theme={'light'}
                color={'grey'}
                size={'lg'}
                icon={{ name: 'Check', position: 'left', size: 'Large', fill: '#34C77E' }}
              >
                {'간편 문자 보내기'}
              </Chip>
            </ChipContainer>
            <HeroBenefitDescription>
              {'논 평수 측정부터 도착 문자까지,\n한눈에 확인하고 쉽게 관리할 수 있어요!'}
            </HeroBenefitDescription>
          </HeroBenefitGroup>

          <Button
            borderType={'square'}
            color={'filledPrimaryStrong'}
            text={{ content: '내 대행작업 관리하기', size: 'md' }}
            size={'md'}
            onClick={() => {
              ampli.pvmClickManagedFarmButton();
              navigate(managedFarmPageInfo.myTaskPage.path);
            }}
          />
        </HeroSection>
      </MainSection>
      <FAQContainer id={'managed-farm-entry-faq'}>
        <FAQTitle>{'자주 묻는 질문'}</FAQTitle>
        <Collapse
          items={items}
          bordered={false}
          expandIconPosition={'start'}
          size={'small'}
          expandIcon={({ isActive }) =>
            (
              <AngleIconWrapper>
                <Icon name={isActive ? 'AngleUp' : 'AngleDown'} size={'Medium'} fill={'#898B94'} />
              </AngleIconWrapper>
            ) as ReactNode
          }
          style={{ width: '100%', backgroundColor: '#f2f2f7' }}
        />
      </FAQContainer>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: calc(100vh - 48px);
`;

const MainSection = styled.div`
  display: flex;
  padding: 40px 16px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 32px;
  align-self: stretch;
  flex-shrink: 0;
`;

const HeroSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;

  align-self: stretch;
`;

const HeroTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const HeroTitle = styled.div`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/XXX-Large/xxxl-bold */
  font-family: Pretendard;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 31.2px */

  white-space: pre-wrap;
`;

const HeroTitleImage = styled.div<{ imageUrl: string }>`
  width: 320px;
  height: 231px;
  background: ${(props) => `url(${props.imageUrl}) transparent 50% / cover no-repeat;`};
`;

const HeroBenefitGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const ChipContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
`;

const HeroBenefitDescription = styled.div`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Body/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 24px */

  white-space: pre-wrap;
`;

const FAQContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;

  flex: 1;

  padding: 32px 16px;

  background-color: #f2f2f7;
`;

const FAQTitle = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/X-Large/xl-bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 23.4px */
`;

const Question = styled.p`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Body/Large/l-light */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 300;
  line-height: 130%; /* 24px */
`;

const Answer = styled.p`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);

  /* Body/Large/l-light */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 300;
  line-height: 130%; /* 24px */

  padding-top: 8px;
  padding-left: 24px;
`;

const AngleIconWrapper = styled.div`
  width: 20px;
  height: 20px;

  display: flex;
  justify-content: center;
  align-items: center;
`;
